import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLanguage, LANGUAGES } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { BookOpen, Mail, Lock, User, ArrowRight, Calendar, Users, Globe } from 'lucide-react';

const AuthPage = ({ isLogin = true }) => {
    const navigate = useNavigate();
    const { login, register } = useAuth();
    const { language, setLanguage, t } = useLanguage();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [birthday, setBirthday] = useState('');
    const [gender, setGender] = useState('');
    const [selectedLanguage, setSelectedLanguage] = useState(language);
    const [receiveEmails, setReceiveEmails] = useState(true);
    const [rememberMe, setRememberMe] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (isLogin) {
                await login(email, password, rememberMe);
            } else {
                // Set the selected language before registering
                setLanguage(selectedLanguage);
                await register(email, password, name, receiveEmails, birthday, gender, selectedLanguage);
            }
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.detail || 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
            <div className="noise-overlay" />
            
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-md"
            >
                <div className="glass-card rounded-3xl p-8 shadow-xl">
                    <div className="text-center mb-8">
                        <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <BookOpen className="w-8 h-8 text-primary" />
                        </div>
                        <h1 className="font-heading text-3xl font-semibold text-foreground mb-2">
                            {isLogin ? 'Welcome Back' : 'Create Account'}
                        </h1>
                        <p className="text-muted-foreground">
                            {isLogin 
                                ? 'Continue your journaling journey' 
                                : 'Start your journey to emotional wellness'}
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        {!isLogin && (
                            <div className="space-y-2">
                                <Label htmlFor="name" className="text-foreground">Name</Label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                    <Input
                                        id="name"
                                        data-testid="auth-name-input"
                                        type="text"
                                        placeholder="Your name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50"
                                        required
                                    />
                                </div>
                            </div>
                        )}

                        {!isLogin && (
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="birthday" className="text-foreground">Birthday</Label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                        <Input
                                            id="birthday"
                                            data-testid="auth-birthday-input"
                                            type="date"
                                            value={birthday}
                                            onChange={(e) => setBirthday(e.target.value)}
                                            className="pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="gender" className="text-foreground">Gender</Label>
                                    <div className="relative">
                                        <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                        <select
                                            id="gender"
                                            data-testid="auth-gender-select"
                                            value={gender}
                                            onChange={(e) => setGender(e.target.value)}
                                            className="w-full pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50 text-foreground appearance-none cursor-pointer"
                                            style={{ backgroundColor: 'hsl(var(--muted) / 0.3)' }}
                                        >
                                            <option value="">Select...</option>
                                            <option value="M">Male</option>
                                            <option value="F">Female</option>
                                            <option value="Prefer not to say">Prefer not to say</option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Language Selection - Registration only */}
                        {!isLogin && (
                            <div className="space-y-2">
                                <Label htmlFor="language" className="text-foreground">{t('language')}</Label>
                                <div className="relative">
                                    <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                    <select
                                        id="language"
                                        data-testid="auth-language-select"
                                        value={selectedLanguage}
                                        onChange={(e) => setSelectedLanguage(e.target.value)}
                                        className="w-full pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50 text-foreground appearance-none cursor-pointer"
                                        style={{ backgroundColor: 'hsl(var(--muted) / 0.3)' }}
                                    >
                                        {LANGUAGES.map((lang) => (
                                            <option key={lang.code} value={lang.code}>
                                                {lang.flag} {lang.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-foreground">Email</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                <Input
                                    id="email"
                                    data-testid="auth-email-input"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50"
                                    required
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-foreground">Password</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                <Input
                                    id="password"
                                    data-testid="auth-password-input"
                                    type="password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="pl-10 h-12 rounded-xl bg-muted/30 border-transparent focus:border-primary/50"
                                    required
                                    minLength={6}
                                />
                            </div>
                        </div>

                        {/* Remember Me - Login only */}
                        {isLogin && (
                            <div className="flex items-center space-x-3">
                                <Checkbox
                                    id="rememberMe"
                                    data-testid="remember-me-checkbox"
                                    checked={rememberMe}
                                    onCheckedChange={setRememberMe}
                                />
                                <Label htmlFor="rememberMe" className="text-sm text-muted-foreground cursor-pointer">
                                    Remember me for 30 days
                                </Label>
                            </div>
                        )}

                        {!isLogin && (
                            <div className="flex items-center space-x-3">
                                <Checkbox
                                    id="emails"
                                    data-testid="receive-emails-checkbox"
                                    checked={receiveEmails}
                                    onCheckedChange={setReceiveEmails}
                                />
                                <Label htmlFor="emails" className="text-sm text-muted-foreground cursor-pointer">
                                    Send me reminders and friendly emails
                                </Label>
                            </div>
                        )}

                        {error && (
                            <div className="p-3 rounded-xl bg-destructive/10 text-destructive text-sm" data-testid="auth-error">
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            data-testid="auth-submit-btn"
                            className="w-full h-12 rounded-full text-lg font-medium shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                            disabled={loading}
                        >
                            {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
                            <ArrowRight className="ml-2 w-5 h-5" />
                        </Button>
                    </form>

                    <div className="mt-6 text-center">
                        <p className="text-muted-foreground">
                            {isLogin ? "Don't have an account? " : 'Already have an account? '}
                            <Link
                                to={isLogin ? '/register' : '/login'}
                                data-testid="auth-toggle-link"
                                className="text-primary font-medium hover:underline"
                            >
                                {isLogin ? 'Sign up' : 'Sign in'}
                            </Link>
                        </p>
                    </div>
                </div>
            </motion.div>
        </div>
    );
};

export default AuthPage;
